import { motion } from "framer-motion";
import { GraduationCap, Heart, Award } from "lucide-react";
import { IMAGES, CREDENTIALS } from "./data";
import { FloatingCloud, FloatingMoon, Sparkle, SleepZ } from "./SleepDecorations";

export default function AuthoritySection() {
  return (
    <section className="relative py-28 lg:py-36 bg-[#faf6f0] overflow-hidden">
      {/* Sleep decorations */}
      <FloatingCloud
        className="absolute top-0 left-[-30px] text-[#6d28d9] drift-slow"
        size={240}
      />
      <FloatingMoon
        className="absolute top-16 right-[-50px]"
        size={240}
        tone="dark"
      />
      <Sparkle className="absolute top-[15%] right-[20%] text-rose-500/60" size={22} />
      <SleepZ className="absolute bottom-[20%] left-[8%] text-violet-500" size={70} />
      <Sparkle className="absolute bottom-[35%] right-[10%] text-violet-500" size={20} />
      <FloatingCloud
        className="absolute bottom-0 right-[-40px] text-violet-200 drift-slow"
        size={200}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-violet-200 shadow-[0_30px_80px_-20px_rgba(124,58,237,0.35)]">
              <img
                src={IMAGES.marianaPortrait}
                alt="Mariana Mello — Especialista em sono infantil"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a0a2e]/85 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-7">
                <p className="font-display italic text-2xl text-[#1f1430]">Mariana Mello</p>
                <p className="text-violet-200/90 text-sm tracking-wide">
                  Fisioterapeuta · Especialista em Sono Infantil
                </p>
              </div>
            </div>
            {/* Floating tag — subtle, no longer competing with photo */}
            <div className="absolute -bottom-5 -right-5 lg:-right-7 bg-white rounded-2xl px-5 py-3 shadow-[0_15px_40px_-10px_rgba(124,58,237,0.25)] border border-violet-100/80">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#6d28d9]/80 font-bold">
                Acompanhou
              </p>
              <p className="font-display text-lg text-[#1a0a2e] leading-tight">
                <span className="text-[#6d28d9] font-semibold">+600</span>{" "}
                famílias
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <h2 className="font-display text-4xl lg:text-[52px] text-[#1a0a2e] leading-[1.1] mb-6">
              Muito prazer,
              <span className="block text-[#6d28d9] font-semibold mt-1">
                sou Mariana Mello.
              </span>
            </h2>

            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Criadora da <span className="text-violet-700 font-medium">Jornada do Sono</span>,
              cristã, esposa e mãe da Clarice e do André. Como você, já vivi noites difíceis e dias
              cansativos.
            </p>
            <p className="text-slate-600 text-base leading-relaxed mb-8">
              Foi durante a gravidez da minha primeira filha que me apaixonei pelo universo do sono
              infantil. Desde então, essa se tornou minha missão: caminhar ao lado de mães como
              você, levando acolhimento, leveza e conhecimento.
            </p>

            <div className="rounded-2xl bg-white/80 backdrop-blur border border-violet-200/70 p-6 mb-8 shadow-[0_15px_40px_-15px_rgba(124,58,237,0.2)]">
              <h3 className="flex items-center gap-2 text-violet-700 font-semibold text-sm uppercase tracking-wider mb-4">
                <GraduationCap className="w-4 h-4" />
                Um pouco mais sobre mim
              </h3>
              <ul className="space-y-3">
                {CREDENTIALS.map((c, i) => (
                  <li key={i} className="flex gap-3 text-slate-700 text-[14.5px] leading-snug">
                    <Award className="w-4 h-4 text-violet-600 shrink-0 mt-0.5" strokeWidth={2} />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-start gap-3 rounded-2xl bg-gradient-to-br from-violet-100/70 to-rose-100/40 border border-violet-200/70 p-5">
              <Heart
                className="w-5 h-5 text-[#6d28d9] shrink-0 mt-1"
                fill="currentColor"
              />
              <p className="font-display italic text-[#1f1430] text-lg lg:text-xl leading-snug">
                Porque cada família é única.
                <span className="block text-[#4a3a5e] not-italic font-sans text-[14px] lg:text-[15px] mt-2 leading-relaxed">
                  E o sono do seu bebê também merece ser tratado assim.
                </span>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
